import { Injectable } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';

import { AppForm } from '../../../../../../common/forms/app.form';
import {
  ApplicationAppEntityApplicationRead,
  ApplicationAppEntityApplicationWrite
} from '../../../../../../openapi';
import { UnrelatedDeviceForm } from './unrelated-device.form';
import { ParcelItemForm } from '../../components/parcels/parcel-item.form';
import { toNumber } from '../../../../../../services/helpers';
import { environment } from '../../../../../../../environments/environment';
import { applicationSubmittedTo } from '../../../../../../../environments/base.config';
import { CorrespondenceForm } from './correspondence.form';

export function actTypesByServiceUrl(url: string): string[] {
  switch (url) {
    case '/wniosek-o-zmiane-aktu-planowania-przestrzennego': {
      return [
        'Miejscowy plan zagospodarowania przestrzennego',
        'Zintegrowany plan inwestycyjny'
      ];
    }
    case '/uwaga-do-konsultowanego-projaktu-aktu-planowania-przestrzennego': {
      return [
        'Miejscowy plan zagospodarowania przestrzennego',
        'Zintegrowany plan inwestycyjny'
      ];
    }
    case '/wniosek-do-projektu-aktu-planowania-przestrzennego': {
      return [
        'Plan ogólny gminy',
        'Miejscowy plan zagospodarowania przestrzennego'
      ];
    }
    case '/wniosek-o-sporządzenie-aktu-planowania-przestrzennego': {
      return [
        'Plan ogólny gminy',
        'Miejscowy plan zagospodarowania przestrzennego',
        'Zintegrowany plan inwestycyjny',
        'Miejscowy plan rewitalizacji',
        'Uchwała ustalająca zasady i warunki sytuowania obiektów małej architektury, tablic reklamowych i urządzeń reklamowych oraz ogrodzeń, ich gabaryty, standardy jakościowe oraz rodzaje materiałów budowlanych, z jakich mogą być wykonane'
      ];
    }
    default:
      return [
        'Plan ogólny gminy',
        'Miejscowy plan zagospodarowania przestrzennego',
        'Zintegrowany plan inwestycyjny',
        'Miejscowy plan rewitalizacji',
        'Uchwała ustalająca zasady i warunki sytuowania obiektów małej architektury, tablic reklamowych i urządzeń reklamowych oraz ogrodzeń, ich gabaryty, standardy jakościowe oraz rodzaje materiałów budowlanych, z jakich mogą być wykonane',
        'Audyt krajobrazowy'
      ];
  }
}

export type DeviceType = 'Części budynków' | 'Inne urządzenia niebędące sieciami' | 'Sieci/przyłącza' | string;

@Injectable()
export class ApplicationDataForm extends AppForm {

  public deviceTypes: DeviceType[] = [
    'Części budynków',
    'Inne urządzenia niebędące sieciami',
    'Sieci/przyłącza'
  ];

  constructor() {
    super({
      office: new FormControl({ value: environment.applicationSubmittedTo, disabled: true }, { nonNullable: true }),
      documentReceiptMethod: new FormControl('', { validators: [Validators.required], nonNullable: true }),

      //bug fix
      t: new FormControl('')
    });
  }

  public createIssuedDocumentTypeControl(): void {
    this.addControl('issuedDocumentType',
      new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      })
    );
  }

  public get issuedDocumentType(): FormControl | null {
    return this.get('issuedDocumentType') as FormControl | null;
  }

  public createDocumentIssuePurposeControl(): void {
    this.addControl('documentIssuePurpose',
      new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(200)]
      })
    );
  }

  public get documentIssuePurpose(): FormControl | null {
    return this.get('documentIssuePurpose') as FormControl | null;
  }

  public get correspondenceForm(): CorrespondenceForm | null {
    return this.get('correspondenceForm') as CorrespondenceForm | null;
  }

  public get documentReceiptMethod(): FormControl | null {
    return this.get('documentReceiptMethod') as FormControl | null;
  }

  public get ePuapAddress(): FormControl | null {
    return this.get('ePuapAddress') as FormControl | null;
  }

  public get eDeliveryAddress(): FormControl | null {
    return this.get('eDeliveryAddress') as FormControl | null;
  }

  public documentReceiptMethodChange(v: string): void {

    if (v === '4d536cdd-dc3a-42f9-bf89-342e5cc0c885') {
      this.addControl(
        'ePuapAddress',
        new FormControl('', [Validators.required, Validators.maxLength(255)])
      );
    } else {
      this.removeControl('ePuapAddress');
    }

    if (v === '6bef93e4-f125-49f2-ab12-11ddc5e1e8a2') {
      this.addControl('eDeliveryAddress', new FormControl('', [Validators.required, Validators.maxLength(255)]));
    } else {
      this.removeControl('eDeliveryAddress');
    }

    if (v === '6b5e71fd-40f7-4333-91d9-ca5451de6309') {
      this.addControl('correspondenceForm', new CorrespondenceForm());
    } else {
      this.removeControl('correspondenceForm');
    }

  }

  public createApplicationSubjectControl(): void {
    this.addControl('applicationSubject', new FormControl('', {
      validators: [Validators.required, Validators.maxLength(255)],
      nonNullable: true
    }));
  }

  public createApplicationConcernsStreetControl(): void {
    this.addControl('applicationConcernsStreet', new FormControl(null, { validators: [Validators.required, Validators.maxLength(255)] }));
  }

  public get applicationConcernsStreet(): FormControl | null {
    return this.get('applicationConcernsStreet') as FormControl | null;
  }

  public get applicationSubject(): FormControl | null {
    return this.get('applicationSubject') as FormControl | null;
  }

  public showResponsiblePersonControls(): boolean {
    return !!this.responsiblePersonPhone && !!this.responsiblePersonLastName && !!this.responsiblePersonFirstName && !!this.responsiblePersonEmail;
  }

  private get responsiblePersonFirstName(): FormControl | null {
    return this.get('responsiblePersonFirstName') as FormControl | null;
  }

  private get responsiblePersonLastName(): FormControl | null {
    return this.get('responsiblePersonLastName') as FormControl | null;
  }

  private get responsiblePersonPhone(): FormControl | null {
    return this.get('responsiblePersonPhone') as FormControl | null;
  }

  private get responsiblePersonEmail(): FormControl | null {
    return this.get('responsiblePersonEmail') as FormControl | null;
  }

  public createResponsiblePersonControls(): void {
    this.addControl('responsiblePersonFirstName', new FormControl('', [Validators.required, Validators.maxLength(255)]));
    this.addControl('responsiblePersonLastName', new FormControl('', [Validators.required, Validators.maxLength(255)]));
    this.addControl('responsiblePersonPhone', new FormControl(''));
    this.addControl('responsiblePersonEmail', new FormControl('', [Validators.email, Validators.maxLength(255)]));
  }

  private get managerFirstName(): FormControl | null {
    return this.get('managerFirstName') as FormControl | null;
  }

  private get managerLastName(): FormControl | null {
    return this.get('managerLastName') as FormControl | null;
  }

  private get managerPhone(): FormControl | null {
    return this.get('managerPhone') as FormControl | null;
  }

  private get managerEmail(): FormControl | null {
    return this.get('managerEmail') as FormControl | null;
  }

  public showManagerControls(): boolean {
    return !!this.managerFirstName && !!this.managerLastName && !!this.managerPhone && !!this.managerEmail;
  }

  public createManagerControls(): void {
    this.addControl('managerFirstName', new FormControl('', [Validators.required, Validators.maxLength(255)]));
    this.addControl('managerLastName', new FormControl('', [Validators.required, Validators.maxLength(255)]));
    this.addControl('managerPhone', new FormControl('', [Validators.required, Validators.maxLength(255)]));
    this.addControl('managerEmail', new FormControl('', [Validators.email, Validators.maxLength(255)]));
  }

  public get comments(): FormControl | null {
    return this.get('comments') as FormControl | null;
  }

  public createCommentsControl(): void {
    this.addControl('comments', new FormControl('', [Validators.maxLength(5000)]));
  }

  public createUnrelatedDeviceForm(): void {
    this.addControl('unrelatedDeviceForm', new UnrelatedDeviceForm());
  }

  public get unrelatedDeviceForm(): UnrelatedDeviceForm | null {
    return this.get('unrelatedDeviceForm') as UnrelatedDeviceForm | null;
  }

  public createDeviceTypeControl(): void {
    this.addControl('deviceType', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get deviceType(): FormControl | null {
    return this.get('deviceType') as FormControl | null;
  }

  public createActTypeControl(): void {
    this.addControl('actType', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required]
    }));
  }

  public get actType(): FormControl | null {
    return this.get('actType') as FormControl | null;
  }

  public deviceTypeChange(v: DeviceType): void {
    const d1: DeviceType[] = ['Części budynków', 'Inne urządzenia niebędące sieciami'];
    if (d1.includes(v)) {
      this.addControl('deviceTypeDescription', new FormControl(null, [Validators.required, Validators.maxLength(5000)]));
    } else {
      this.removeControl('deviceTypeDescription');
    }
    const d2: DeviceType[] = ['Sieci/przyłącza'];
    if (d2.includes(v)) {
      this.addControl('networkTypeDescription', new FormControl(null, [Validators.required, Validators.maxLength(5000)]));
    } else {
      this.removeControl('networkTypeDescription');
    }
  }

  public get deviceTypeDescription(): FormControl | null {
    return this.get('deviceTypeDescription') as FormControl | null;
  }

  public get networkTypeDescription(): FormControl | null {
    return this.get('networkTypeDescription') as FormControl | null;
  }

  public createLegalInterestControl(): void {
    this.addControl('legalInterest', new FormControl('', [Validators.required, Validators.max(5000)]));
  }

  public get legalInterest(): FormControl | null {
    return this.get('legalInterest') as FormControl | null;
  }

  public toWriteModel(): ApplicationAppEntityApplicationWrite {
    return {
      ...this.correspondenceForm?.toWriteModel(),
      applicationSubject: this.value?.applicationSubject || null,
      documentReceiptMethod: this.value?.documentReceiptMethod,
      responsiblePersonFirstName: this.value?.responsiblePersonFirstName,
      responsiblePersonLastName: this.value?.responsiblePersonLastName,
      responsiblePersonPhone: this.value?.responsiblePersonPhone,
      responsiblePersonEmail: this.value?.responsiblePersonEmail,
      managerEmail: this.value?.managerEmail,
      managerPhone: this.value?.managerPhone,
      managerFirstName: this.value?.managerFirstName,
      managerLastName: this.value?.managerLastName,
      notes: this.value?.comments,
      deviceTypeDescription: this.value?.deviceTypeDescription,
      networkOrConnectionTypeDescription: this.value?.networkTypeDescription,
      legalInterest: this.legalInterest?.value,
      actType: this.actType?.value || null,
      ePuapAddress: this.ePuapAddress?.value || null,
      eDeliveryAddress: this.eDeliveryAddress?.value || null,
      applicationConcernsStreet: this.applicationConcernsStreet?.value || null,
      deviceType: this.unrelatedDeviceForm?.deviceType.value || this.value?.deviceType,
      roadwayWidth: toNumber(this.unrelatedDeviceForm?.roadwayWidth.value),
      roadwayLength: toNumber(this.unrelatedDeviceForm?.roadwayLength.value),
      roadwayArea: toNumber(this.unrelatedDeviceForm?.roadwayArea.value),
      sidewalkWidth: toNumber(this.unrelatedDeviceForm?.sidewalkWidth.value),
      sidewalkLength: toNumber(this.unrelatedDeviceForm?.sidewalkLength.value),
      sidewalkArea: toNumber(this.unrelatedDeviceForm?.sidewalkArea.value),
      remainingElementsWidth: toNumber(this.unrelatedDeviceForm?.remainingElementsWidth.value),
      remainingElementsLength: toNumber(this.unrelatedDeviceForm?.remainingElementsLength.value),
      remainingElementsArea: toNumber(this.unrelatedDeviceForm?.remainingElementsArea.value),
      bridgeStructureWidth: toNumber(this.unrelatedDeviceForm?.bridgeStructureWidth.value),
      bridgeStructureLength: toNumber(this.unrelatedDeviceForm?.bridgeStructureLength.value),
      bridgeStructureArea: toNumber(this.unrelatedDeviceForm?.bridgeStructureArea.value),
      placementDate: this.unrelatedDeviceForm?.placementDate.value,
      issuedDocumentType: this.issuedDocumentType?.value || null,
      documentIssuePurpose: this.documentIssuePurpose?.value || null
    };
  }

  public override patchFormValues(app: ApplicationAppEntityApplicationRead): void {
    this.patchValue({
      office: environment.applicationSubmittedTo,
      documentReceiptMethod: app?.documentReceiptMethod?.id || '',
      applicationSubject: app?.applicationSubject || '',
      responsiblePersonFirstName: app?.responsiblePersonFirstName,
      responsiblePersonLastName: app?.responsiblePersonLastName,
      responsiblePersonPhone: app?.responsiblePersonPhone,
      responsiblePersonEmail: app?.responsiblePersonEmail,
      managerEmail: app?.managerEmail,
      managerPhone: app?.managerPhone,
      managerFirstName: app?.managerFirstName,
      managerLastName: app?.managerLastName,
      comments: app?.notes,
      deviceType: app?.deviceType || '',
      legalInterest: app?.legalInterest,
      actType: app?.actType || '',
      applicationConcernsStreet: app?.applicationConcernsStreet,
      issuedDocumentType: app?.issuedDocumentType || '',
      documentIssuePurpose: app?.documentIssuePurpose || ''
    });

    this.deviceTypeChange(app?.deviceType || '');

    this.deviceTypeDescription?.setValue(app?.deviceTypeDescription);
    this.networkTypeDescription?.setValue(app?.networkOrConnectionTypeDescription);

    this.unrelatedDeviceForm?.patchFormValues(app);

    this.documentReceiptMethodChange(app?.documentReceiptMethod?.id || '');
    this.ePuapAddress?.setValue(app?.ePuapAddress || '');
    this.eDeliveryAddress?.setValue(app?.eDeliveryAddress);
    this.correspondenceForm?.patchFormValues(app);
  }
}
